// src/components/aiTalentDrive/JobDescriptionModal.tsx
// Job description pop-up for an AI Talent Drive track.
import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import "../../styles/hiring-circuit.css";
import {
  JOB_DESCRIPTIONS,
  accentFor,
  bandsFor,
  isShortTerm,
  splitTrack,
} from "./tracks";

export default function JobDescriptionModal({
  track,
  initialBand = "",
  onClose,
  onRegister = null,
  forCandidates = true,
  themeClass = "",
}: {
  track: string;
  // One of the track's bands; the band the reader had already picked, if any.
  initialBand?: string;
  onClose: () => void;
  onRegister?: ((band: string) => void) | null;
  // The candidate page shows the track code; the company page shows just
  // the role and its description.
  forCandidates?: boolean;
  // Extra class for the backdrop: a page that re-themes .hc-root (the drive
  // page's "td") passes it so the portalled modal picks the theme up too.
  themeClass?: string;
}) {
  const { code, name } = splitTrack(track);
  const accent = accentFor(track);
  const closeRef = useRef<HTMLButtonElement>(null);
  const bands = bandsFor(track);
  const [band, setBand] = useState(
    bands.includes(initialBand) ? initialBand : bands[0] || ""
  );
  const hasAnyJd = !!JOB_DESCRIPTIONS[track];
  const jd = JOB_DESCRIPTIONS[track]?.[band];

  useEffect(() => {
    closeRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [onClose]);

  // Portalled to <body>: .hc-root is its own stacking context (isolation),
  // so anything rendered inside it sits under the site's fixed header.
  return createPortal(
    <div className={`hc-root jd-backdrop${themeClass ? ` ${themeClass}` : ""}`} onClick={onClose}>
      <div
        className="jd-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="jd-title"
        onClick={(e) => e.stopPropagation()}
        style={{ "--tag": accent } as React.CSSProperties}
      >
        <div className="jd-head">
          <div>
            {forCandidates && <span className="role-tag-code">{code}</span>}
            <h3 id="jd-title">{name}</h3>
            {hasAnyJd && (
              <p className="jd-meta">
                Multiple Locations · {isShortTerm(track) ? "Internship" : "Full-time"} ·{" "}
                {band.endsWith("years") ? `${band} experience` : band}
              </p>
            )}
            {hasAnyJd && bands.length > 1 && (
              <div className="jd-bands" role="group" aria-label="Experience level">
                {bands.map((opt) => (
                  <button
                    key={opt}
                    type="button"
                    className="jd-band"
                    aria-pressed={opt === band}
                    onClick={() => setBand(opt)}
                  >
                    {opt}
                  </button>
                ))}
              </div>
            )}
          </div>
          <button
            ref={closeRef}
            type="button"
            className="jd-close"
            aria-label="Close"
            onClick={onClose}
          >
            &times;
          </button>
        </div>

        <div className="jd-body">
          {jd ? (
            <>
              <h4>Key skills</h4>
              <ul>
                {jd.skills.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
              <h4>Preferred qualifications</h4>
              <ul>
                {jd.qualifications.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
              <h4>Typical tools</h4>
              <p className="jd-note">Examples, not requirements.</p>
              <ul>
                {jd.tools.map((t) => (
                  <li key={t.text}>
                    {t.label && <strong>{t.label}: </strong>}
                    {t.text}
                  </li>
                ))}
              </ul>
            </>
          ) : (
            <p className="jd-soon">
              The full job description for this track will be published here
              shortly.
            </p>
          )}
        </div>

        {onRegister && (
          <div className="jd-foot">
            <button type="button" className="btn btn-copper" onClick={() => onRegister(band)}>
              Register for this track
            </button>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}
